import React, { useEffect, useState } from "react"
import { useTranslation } from "next-i18next"
import { Button } from "../formElements/button"
import { Authentication, AuthenticatonPage } from "../authentication"
import { Modal } from "../modal"
import { LottieAnimation } from "../lottieAnimation"
import animationData from "../../assets/dashboard/wave.json"
import { isMobile } from "react-device-detect"
import { ReactComponent as DiscmakersSVG } from "../../assets/partners/discmakers.svg"
import { ReactComponent as SymphonicSVG } from "../../assets/partners/symphonic.svg"
import { ReactComponent as LogoDefaultSVG } from "../../assets/logo/bonsai-default.svg"
import { ReactComponent as CrossSVG } from "../../assets/partners/cross.svg"
import tw, { styled, css, theme } from "twin.macro"
import { useRouter } from "next/router"
import { Events, registerEvent } from "../../analytics"

export enum Partner {
  Symphonic = "symphonic",
  Discmakers = "discmakers",
}

const LogoDefault = styled(LogoDefaultSVG)(() => [
  tw`
    w-[118px]
    tablet:(w-36)
    tablet-lg:(w-[295px])
  `,
  css`
    path {
      fill: ${theme(`colors.logo-color`)};
    }
  `,
])

const SymphonicLogo = styled(SymphonicSVG)(() => [
  tw`
    w-[118px]
    tablet:(w-36)
    tablet-lg:(w-[295px])
  `,
])
const DiscmakersLogo = styled(DiscmakersSVG)(() => [
  tw`
    w-[144px]
    tablet:(w-60)
    tablet-lg:(w-[400px])
  `,
])

const Cross = styled(CrossSVG)(() => [
  tw`
    w-[20px]
    tablet:(w-[24px])
    tablet-lg:(w-[36px])
  `,
])

interface PartnerLogoProps {
  partner: string
}
const PartnerLogos = ({ partner }: PartnerLogoProps) => {
  const { t } = useTranslation("explore")
  switch (partner) {
    case Partner.Discmakers:
      return (
        <h1 tw="text-2xl space-x-4 tablet:(text-4xl space-x-8) desktop:(text-[96px]) text-center flex items-center justify-center left-0 tablet:(left-[-50px]) tablet-lg:(left-[-53px]) relative">
          <span>
            <span tw="left-[-9999px] absolute">{t("partner.hero.discmakers.h1alt")}</span>
            <DiscmakersLogo />
          </span>
          <Cross />
          <LogoDefault />
        </h1>
      )
    default:
      return (
        <h1 tw="text-2xl space-x-4 tablet:(text-4xl space-x-8) desktop:(text-[96px]) text-center flex items-center justify-center">
          <span>
            <span tw="left-[-9999px] absolute">{t("partner.hero.symphonic.h1alt")}</span>
            <SymphonicLogo />
          </span>
          <Cross />
          <LogoDefault />
        </h1>
      )
  }
}

interface HeroSectionProps {
  token: boolean
}

const HeroSection = ({ token }: HeroSectionProps) => {
  const { t } = useTranslation("explore")
  const [show, setShow] = useState(false)
  const [isNotDismissable, setNotDismissable] = useState(false)
  const router = useRouter()
  const partner = router.pathname.replace("/", "")
  const modalProps = {
    show,
    setShow,
    notDismissable: isNotDismissable,
    component: <Authentication view={AuthenticatonPage.Join} setShow={setShow} setNotDismissable={setNotDismissable} />,
  }

  const navToProfile = async () => {
    await router.push({ pathname: "/me" })
  }

  useEffect(() => {
    switch (partner) {
      case "discmakers":
        registerEvent(Events.VisitedDiscmakers)
        break
      case "symphonic":
        registerEvent(Events.VisitedSymphonic)
        break
      default:
    }
  }, [])

  return (
    <div data-testid="dashboard-hero" tw="relative flex flex-col justify-center items-center">
      <div tw="absolute top-[50px] tablet:(top-[35px]) desktop:(top-[10px]) w-full overflow-hidden">
        <LottieAnimation width={isMobile ? "200%" : "110%"} height="auto" animationData={animationData} />
      </div>
      <div tw="space-y-8 tablet-lg:(space-y-14) z-10 px-3.5 py-16 flex flex-col justify-center items-center">
        <div tw="space-y-4 tablet:(space-y-6)">
          <PartnerLogos partner={partner} />

          <h2 tw="text-center text-lg font-normal tablet:(text-xl) desktop:(text-2xl font-black)">
            {t("partner.hero.h4")}
          </h2>
        </div>
        {!token ? (
          <Button action={t("partner.hero.button")} onClick={() => setShow(true)} />
        ) : (
          <Button action={t("partner.hero.show_profile_cta")} onClick={async () => await navToProfile()} />
        )}
        <Modal {...modalProps} />
      </div>
    </div>
  )
}

export default HeroSection
